/**
 * Ticket Lifecycle v2 — Slice 5A.2 (+ slice 5A.6-lite UI widening +
 * slice 5C Lifecycle OptiTime tile)
 *
 * Read-only display card showing lifecycle time buckets for a single
 * ticket. Mounts inside `TicketDetailsPopup`. Flag-gated by
 * `custodyHandoff` — renders nothing when the flag is off, so the
 * popup looks identical to its pre-slice-5A.2 appearance for users
 * without the flag.
 *
 * Data flow:
 *   `useTicketCustodyEvents(ticketId)`        → custody events
 *   `useTicketJobsForLifecycle(ticketId)`     → enriched job rows
 *   `useTicketBusinessState(ticketId)`        → approval gate
 *   `deriveTimeBuckets(...)`                  → bucket minutes
 *   `classifyTicketJobMix(ticket, jobs)`      → bucket + label
 *   `computeRemainingApprovedWorkMinutes(...)` → overlap minutes (or null)
 *   `buildAdvisorDelayImpactDisplayState(...)` → Advisor Delay tile
 *   `buildLifecycleOptiTimeDisplayState(...)`  → Lifecycle OptiTime tile
 *
 * Lifecycle time is **display only**. The header copy explicitly
 * states it does not change technician scheduling. This is the
 * load-bearing distinction between Tech OptiTime (existing
 * `tickets.opti_time` → Chronomancer) and Lifecycle OptiTime (the
 * new buckets / new estimate).
 *
 * Boundary: imports only the lifecycle helpers + read-only hooks +
 * `useFeatureFlag`. No scheduling / timer / parking / rollover / bay
 * code.
 */
import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useTicketCustody } from '@/hooks/tickets/useTicketCustody';
import { useTicketCustodyEvents } from '@/hooks/tickets/useTicketCustodyEvents';
import { useTicketCustodyHistory } from '@/hooks/tickets/useTicketCustodyHistory';
import { CustodyHistoryPanel } from '@/components/tickets/CustodyHistoryPanel';
import { useCustodyActorNames } from '@/hooks/tickets/useCustodyActorNames';
import { useTicketJobsForLifecycle } from '@/hooks/tickets/useTicketJobsForLifecycle';
import { useTicketBusinessState } from '@/hooks/tickets/useTicketBusinessState';
import { useAdvisorBucketMetrics } from '@/hooks/tickets/useAdvisorBucketMetrics';
import {
  deriveTimeBuckets,
} from '@/lib/tickets/lifecycle/deriveTimeBuckets';
import { classifyTicketJobMix } from '@/lib/tickets/lifecycle/jobMixClassifier';
import { computeRemainingApprovedWorkMinutes } from '@/lib/tickets/lifecycle/remainingApprovedWork';
import { buildAdvisorDelayImpactDisplayState } from '@/lib/tickets/lifecycle/advisorDelayImpactDisplay';
import {
  buildLifecycleOptiTimeDisplayState,
  getLifecycleOptiTimeDelayBadge,
} from '@/lib/tickets/lifecycle/lifecycleOptiTimeDisplay';
import type { LifecycleOptiTimePredictedDelay } from '@/lib/tickets/lifecycle/lifecycleOptiTime';

interface Props {
  ticketId: string;
  /** From `calculateOptiTime` prop on the popup — already the
   *  break-adjusted wall-clock value used by Chronomancer. */
  optiTimeMinutes: number;
  /** ISO timestamp of `tickets.created_at`. */
  createdAt: string | null;
  /** Optional terminal-at — when present, replaces `referenceTime`
   *  for closed/voided tickets. */
  terminalAt?: string | null;
  /** Slice 5A.6-lite UI: comeback override input for job-mix
   *  classification. Accepts either camelCase or snake_case shapes
   *  from the caller. */
  isComeback?: boolean | null;
}

/**
 * Friendly time formatter shared with the AdvisorActivityCard pattern.
 * Exported so a future shared helper module can absorb both copies if
 * we consolidate.
 */
export function formatLifecycleMinutes(value: number): string {
  if (value <= 0) return '—';
  if (value < 1) return '< 1m';
  if (value < 60) return `${Math.round(value)}m`;
  const hours = Math.floor(value / 60);
  const minutes = Math.round(value % 60);
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-muted/30 rounded-lg p-3">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export function TicketLifecycleTimeCard({
  ticketId,
  optiTimeMinutes,
  createdAt,
  terminalAt,
  isComeback,
}: Props) {
  const custodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  const advisorTimingAnalyticsEnabled = useFeatureFlag('advisorTimingAnalytics');
  const { activeShop } = useAuth();
  const shopId = activeShop?.id ?? null;
  const { data: events, isLoading } = useTicketCustodyEvents(
    ticketId,
    custodyHandoffEnabled,
  );
  // CUS-2: full handoff history (actor / role / reason) for the
  // accountability trail. Distinct from `events` (timing-only).
  const { data: custodyHistory, isLoading: historyLoading } =
    useTicketCustodyHistory(ticketId, custodyHandoffEnabled);
  // Who did each handoff, by name (role only when the name is unknown).
  const { data: custodyActorNames } = useCustodyActorNames(
    (custodyHistory ?? []).map((e) => e.actorId),
  );
  const { data: jobRows } = useTicketJobsForLifecycle(
    ticketId,
    custodyHandoffEnabled,
  );
  const { data: bizState } = useTicketBusinessState(
    custodyHandoffEnabled ? ticketId : null,
  );
  // Slice 5D-4: read the existing custody row to identify the current
  // FOH advisor for the prediction lookup. Cached by React Query —
  // the kebab + queue surfaces already query the same key.
  const { data: custody } = useTicketCustody(
    custodyHandoffEnabled ? ticketId : null,
  );

  // Reference time for open intervals (currently-pending tickets).
  // Captured per render — the helper is pure, so the value is
  // deterministic for a given render pass.
  const referenceTime = useMemo(() => new Date().toISOString(), []);

  // Compute buckets unconditionally so we can decide the empty state
  // from the result. When flag is off we return null before render.
  const buckets = useMemo(
    () =>
      deriveTimeBuckets({
        optiTime: optiTimeMinutes,
        createdAt,
        terminalAt: terminalAt ?? null,
        referenceTime,
        custodyEvents: events ?? [],
      }),
    [optiTimeMinutes, createdAt, terminalAt, referenceTime, events],
  );

  // Slice 5A.6-lite UI: classify job-mix + compute remaining approved
  // work + build the display state for the "Advisor Delay" tile.
  // Slice 5D-4: jobMix hoisted to its own useMemo so the bucket key
  // (`jobMix.bucket`) is also available to the prediction lookup
  // below.
  const jobMix = useMemo(() => {
    if (!jobRows) return null;
    const flatJobs = jobRows.map((row) => ({
      category: row.jobs?.category ?? null,
      submenu: row.jobs?.submenu ?? null,
      proficiency_category: row.jobs?.proficiency_category ?? null,
      name: row.jobs?.name ?? null,
    }));
    return classifyTicketJobMix({ isComeback: isComeback ?? false }, flatJobs);
  }, [jobRows, isComeback]);

  const advisorDelayDisplay = useMemo(() => {
    if (!jobRows || !jobMix) {
      return null;
    }
    const flatJobs = jobRows.map((row) => ({
      job_status: row.job_status,
      predicted_minutes: row.predicted_minutes,
      minutes_override: row.minutes_override,
      actual_minutes: row.actual_minutes,
      standard_minutes: row.jobs?.standard_minutes ?? null,
    }));
    const remainingApprovedWork = computeRemainingApprovedWorkMinutes({
      jobs: flatJobs,
    });
    return buildAdvisorDelayImpactDisplayState({
      advisorObservedMinutes: buckets.advisorObservedMinutes,
      jobMix,
      remainingApprovedWork,
      businessState: bizState?.current_state ?? null,
    });
  }, [jobRows, jobMix, buckets.advisorObservedMinutes, bizState?.current_state]);

  // Slice 5D-4: predicted advisor delay path. Only fires when:
  //   - custodyHandoff + advisorTimingAnalytics are both on
  //   - the ticket is currently held by a FOH advisor (holder_user_id known)
  //   - observed advisor delay is unavailable (Advisor Delay tile didn't commit)
  // Otherwise the bucket-metrics query stays `enabled: false` and pays
  // zero network cost.
  const observedAdvisorDelayMinutes =
    advisorDelayDisplay?.reason === 'value'
      ? advisorDelayDisplay.valueMinutes
      : null;
  const currentAdvisorUserId =
    custody?.custody_owner === 'FOH' ? custody.holder_user_id : null;
  const wantPrediction =
    advisorTimingAnalyticsEnabled &&
    observedAdvisorDelayMinutes === null &&
    currentAdvisorUserId !== null &&
    jobMix !== null;
  const { data: bucketMetrics } = useAdvisorBucketMetrics({
    shopId: wantPrediction ? shopId : null,
    advisorUserId: wantPrediction ? currentAdvisorUserId : null,
  });

  const predictedAdvisorDelay = useMemo<LifecycleOptiTimePredictedDelay | null>(() => {
    if (!wantPrediction || !bucketMetrics || !jobMix) return null;
    const bucketMetric = bucketMetrics[jobMix.bucket];
    if (!bucketMetric) return null;
    return {
      minutes: bucketMetric.averageAdvisorDelayImpactMinutes,
      confidence: bucketMetric.confidence,
      bucket: jobMix.bucket,
      bucketLabel: jobMix.label,
      sampleSize: bucketMetric.delayImpactSampleSize,
    };
  }, [wantPrediction, bucketMetrics, jobMix]);

  // Slice 5C: Lifecycle OptiTime tile state. Combines Tech OptiTime
  // with the Advisor Delay value from the display helper above. When
  // the Advisor Delay tile refused to commit (`valueMinutes === null`,
  // e.g. `business_state < approved`, missing job mix, or
  // `diag_plus_work` without remaining-approved-work), the lifecycle
  // helper falls back to `'tech_only'` and the tile renders a clearly
  // labeled lower-bound estimate. We pass `null` rather than coercing
  // to 0 so the helper can record the gap.
  //
  // Slice 5C.1: also pass `advisorDelayReason` so the lower-bound note
  // can name the *specific* reason advisor delay is unavailable instead
  // of a generic message.
  //
  // Slice 5D-4: when observed advisor delay is unavailable but a usable
  // historical prediction exists for the ticket's bucket, pass the
  // prediction into the helper. The helper's branch precedence is
  // observed > predicted > tech-only; the display helper labels the
  // result clearly as "predicted" rather than "observed".
  const lifecycleOptiTimeDisplay = useMemo(() => {
    const advisorDelayMinutes =
      advisorDelayDisplay?.reason === 'value'
        ? advisorDelayDisplay.valueMinutes
        : null;
    return buildLifecycleOptiTimeDisplayState({
      techOptiTimeMinutes: optiTimeMinutes,
      advisorDelayImpactMinutes: advisorDelayMinutes,
      predictedAdvisorDelay,
      advisorDelayReason: advisorDelayDisplay?.reason ?? null,
    });
  }, [optiTimeMinutes, advisorDelayDisplay, predictedAdvisorDelay]);

  if (!custodyHandoffEnabled) return null;

  const hasCustodyActivity =
    buckets.fohPendingAcceptMinutes > 0 ||
    buckets.fohActiveMinutes > 0 ||
    buckets.bohPendingAcceptMinutes > 0;

  return (
    <div className="space-y-3" data-testid="ticket-lifecycle-time-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Ticket Lifecycle Time
        </h3>
      </div>

      {isLoading ? (
        <div className="bg-muted/30 rounded-lg p-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading lifecycle time…
        </div>
      ) : (
        <>
          {/* Always-available row: tech-side and cycle.
              Slice 5D-8: label is "Tech OptiTime" (not "Tech Time")
              so the separation from "Lifecycle OptiTime" below is
              unambiguous. The two are different concepts and the UI
              must never let a user confuse them. */}
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Tech OptiTime" value={formatLifecycleMinutes(buckets.executionMinutes)} />
            <Stat label="Total Ticket Time" value={formatLifecycleMinutes(buckets.cycleMinutes)} />
          </div>

          {/* Slice 5C: Lifecycle OptiTime headline — estimated
              vehicle-in-shop time. Display-only; does NOT change
              technician scheduling, OptiFinish, smart assign, waiter
              EOD, or calendar capacity.
              Slice 5D-8: a small breakdown badge ("+Xm observed advisor
              delay" / "+Xm predicted advisor delay") renders alongside
              the value when the lifecycle estimate includes an advisor
              component, so the math is visible without the user having
              to dig into the note. */}
          {lifecycleOptiTimeDisplay.shouldRender && (
            <div
              className="bg-muted/30 rounded-lg p-3 space-y-1"
              data-testid="lifecycle-opti-time-tile"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-muted-foreground">
                  {lifecycleOptiTimeDisplay.label}
                  <span className="ml-1 text-muted-foreground/70">
                    · {lifecycleOptiTimeDisplay.sublabel}
                  </span>
                </div>
                <div className="text-base font-semibold tabular-nums">
                  {lifecycleOptiTimeDisplay.valueMinutes === null
                    ? '—'
                    : `${lifecycleOptiTimeDisplay.lowerBound ? '≥ ' : ''}${formatLifecycleMinutes(
                        lifecycleOptiTimeDisplay.valueMinutes,
                      )}`}
                </div>
              </div>
              {(() => {
                const badge = getLifecycleOptiTimeDelayBadge(lifecycleOptiTimeDisplay);
                if (!badge) return null;
                return (
                  <div
                    className="flex justify-end"
                    data-testid="lifecycle-opti-time-delay-badge"
                    data-delay-kind={badge.kind}
                  >
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      +{formatLifecycleMinutes(badge.minutes)} {badge.kind} advisor delay
                    </span>
                  </div>
                );
              })()}
              <p className="text-[11px] text-muted-foreground">
                {lifecycleOptiTimeDisplay.note}
              </p>
            </div>
          )}

          {hasCustodyActivity ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat
                label="Waiting for Advisor"
                value={formatLifecycleMinutes(buckets.fohPendingAcceptMinutes)}
              />
              <Stat
                label="Advisor Time"
                value={formatLifecycleMinutes(buckets.fohActiveMinutes)}
              />
              <Stat
                label="Waiting for Tech"
                value={formatLifecycleMinutes(buckets.bohPendingAcceptMinutes)}
              />
              <Stat
                label="Advisor Lifecycle Time"
                value={formatLifecycleMinutes(buckets.advisorObservedMinutes)}
              />
              <Stat
                label="BOH / Shop Time"
                value={formatLifecycleMinutes(buckets.bohCustodyMinutes)}
              />
            </div>
          ) : (
            <div className="bg-muted/30 rounded-lg p-3 text-sm text-muted-foreground">
              No handoff activity yet.
            </div>
          )}

          {advisorDelayDisplay?.shouldRender && (
            <div
              className="bg-muted/30 rounded-lg p-3 space-y-1"
              data-testid="advisor-delay-impact-tile"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-muted-foreground">
                  Advisor Delay
                  {advisorDelayDisplay.bucketLabel && (
                    <span className="ml-1 text-muted-foreground/70">
                      · {advisorDelayDisplay.bucketLabel}
                    </span>
                  )}
                </div>
                <div className="text-base font-semibold tabular-nums">
                  {advisorDelayDisplay.valueMinutes === null
                    ? '—'
                    : formatLifecycleMinutes(advisorDelayDisplay.valueMinutes)}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {advisorDelayDisplay.note}
              </p>
            </div>
          )}

          {/* CUS-2: per-ticket custody handoff history — who handed off to
              whom and why. The data was always logged in
              ticket_custody_events; this surfaces the actor/role/reason
              trail that was previously invisible in-app. */}
          <div className="space-y-2" data-testid="custody-history-section">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Custody Handoffs
            </h4>
            <CustodyHistoryPanel
              entries={custodyHistory ?? []}
              isLoading={historyLoading}
              names={custodyActorNames}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Lifecycle time is for visibility only and does not change technician scheduling.
          </p>
        </>
      )}
    </div>
  );
}
